import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, Divider, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, TextField, InputAdornment,
  CircularProgress, Alert, Tooltip, Switch,
} from '@mui/material';
import { People, Security, Search, PersonAdd, Delete, Edit, Email } from '@mui/icons-material';
import { useAuth, ROLE_LABELS, ROLE_DESCRIPTIONS } from '../hooks/AuthContext';
import { fetchUsers, fetchAdUsers, upsertUser, deleteUser as apiDeleteUser, updateUserNotifications } from '../services/api';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();

  const [tmsUsers, setTmsUsers] = useState([]);
  const [adUsers, setAdUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [searchQuery, setSearchQuery] = useState('');
  const [adLoading, setAdLoading] = useState(false);

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#C62828';
      case 'editor': return '#FF6F00';
      case 'viewer': return '#1565C0';
      default: return '#757575';
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const users = await fetchUsers();
      setTmsUsers(users);
    } catch (error) {
      toast.error('שגיאה בטעינת משתמשים');
    } finally {
      setLoading(false);
    }
  };

  const loadAdUsers = async () => {
    try {
      setAdLoading(true);
      const users = await fetchAdUsers();
      setAdUsers(users);
    } catch (error) {
      toast.error('שגיאה בטעינת משתמשי Azure AD');
    } finally {
      setAdLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleAddUser = async () => {
    if (!selectedUser) return;
    try {
      await upsertUser(selectedUser.email, selectedUser.displayName, selectedRole);
      toast.success(`${selectedUser.displayName} נוסף בהצלחה`);
      setAddDialogOpen(false);
      setSelectedUser(null);
      setSelectedRole('viewer');
      loadUsers();
    } catch (error) {
      toast.error('שגיאה בהוספת משתמש');
    }
  };

  const handleEditRole = async () => {
    if (!selectedUser) return;
    try {
      await upsertUser(selectedUser.email, selectedUser.displayName, selectedRole);
      toast.success('ההרשאה עודכנה בהצלחה');
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error('שגיאה בעדכון הרשאה');
    }
  };

  const handleToggleNotification = async (user) => {
    const newValue = !user.receiveNotifications;
    setTmsUsers(prev => prev.map(u => u.email === user.email ? { ...u, receiveNotifications: newValue } : u));
    try {
      await updateUserNotifications(user.email, newValue);
    } catch (error) {
      setTmsUsers(prev => prev.map(u => u.email === user.email ? { ...u, receiveNotifications: !newValue } : u));
      toast.error('שגיאה בעדכון הגדרות התראות');
    }
  };

  const handleDeleteUser = async (email, name) => {
    if (!window.confirm(`האם להסיר את הגישה של ${name}?`)) return;
    try {
      await apiDeleteUser(email);
      toast.success(`${name} הוסר בהצלחה`);
      loadUsers();
    } catch (error) {
      toast.error(error.message || 'שגיאה בהסרת משתמש');
    }
  };

  const filteredAdUsers = adUsers.filter(u =>
    !u.hasTmsAccess &&
    u.email &&
    (u.displayName?.includes(searchQuery) || u.email?.includes(searchQuery))
  );

  const RoleSelector = ({ value, onChange }) => (
    <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
      {['admin', 'editor', 'viewer'].map(role => (
        <FormControlLabel
          key={role}
          value={role}
          control={<Radio sx={{ color: getRoleColor(role), '&.Mui-checked': { color: getRoleColor(role) } }} />}
          label={
            <Box sx={{ py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight={600}>{ROLE_LABELS[role]}</Typography>
                <Chip label={role} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: getRoleColor(role) + '15', color: getRoleColor(role) }} />
              </Box>
              <Typography variant="caption" color="text.secondary">{ROLE_DESCRIPTIONS[role]}</Typography>
            </Box>
          }
          sx={{
            mb: 1, p: 1.5, mx: 0, borderRadius: 2,
            border: `1px solid ${value === role ? getRoleColor(role) + '50' : theme.palette.divider}`,
            bgcolor: value === role ? getRoleColor(role) + '08' : 'transparent',
          }}
        />
      ))}
    </RadioGroup>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>הגדרות מערכת</Typography>

      {/* User Management */}
      <Card sx={{ border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <People sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight={600}>ניהול משתמשים</Typography>
            </Box>
            <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setAddDialogOpen(true); loadAdUsers(); }} size="small">
              הוסף משתמש
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {tmsUsers.length} משתמשים מורשים במערכת
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>משתמש</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>אימייל</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>הרשאה</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>פעולות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tmsUsers.map(user => (
                    <TableRow key={user.email} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 30, height: 30, bgcolor: getRoleColor(user.role), fontSize: '0.75rem' }}>
                            {user.displayName?.charAt(0) || '?'}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>{user.displayName || '—'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                      <TableCell>
                        <Chip label={ROLE_LABELS[user.role] || user.role} size="small" sx={{ bgcolor: getRoleColor(user.role) + '15', color: getRoleColor(user.role), fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="שנה הרשאה">
                          <IconButton size="small" onClick={() => { setSelectedUser(user); setSelectedRole(user.role); setEditDialogOpen(true); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user.email !== 'haimgw@wxg.co.il' && (
                          <Tooltip title="הסר גישה">
                            <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.email, user.displayName)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Email Notification Management - Admin only */}
      {currentUser?.role === 'admin' && (
        <Card sx={{ border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Email sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight={600}>ניהול התראות מייל</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              בחר אילו משתמשים יקבלו התראות מייל על מועדי הגשה קרובים
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>משתמש</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>אימייל</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>הרשאה</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>קבלת התראות</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tmsUsers.map(user => (
                      <TableRow key={user.email} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 30, height: 30, bgcolor: getRoleColor(user.role), fontSize: '0.75rem' }}>
                              {user.displayName?.charAt(0) || '?'}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>{user.displayName || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                        <TableCell>
                          <Chip label={ROLE_LABELS[user.role] || user.role} size="small" sx={{ bgcolor: getRoleColor(user.role) + '15', color: getRoleColor(user.role), fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={user.receiveNotifications ? 'השבת התראות' : 'הפעל התראות'}>
                            <Switch
                              size="small"
                              checked={user.receiveNotifications ?? ['admin', 'editor'].includes(user.role)}
                              onChange={() => handleToggleNotification(user)}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* My Info */}
      <Card sx={{ border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Security sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>פרטי המשתמש שלי</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1.5, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">שם:</Typography>
            <Typography variant="body2" fontWeight={500}>{currentUser?.name}</Typography>
            <Typography variant="body2" color="text.secondary">אימייל:</Typography>
            <Typography variant="body2" fontWeight={500}>{currentUser?.email}</Typography>
            <Typography variant="body2" color="text.secondary">הרשאה:</Typography>
            <Chip label={ROLE_LABELS[currentUser?.role]} size="small" sx={{ width: 'fit-content', bgcolor: getRoleColor(currentUser?.role) + '15', color: getRoleColor(currentUser?.role), fontWeight: 600 }} />
          </Box>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>הוספת משתמש חדש</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {!selectedUser ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>בחר משתמש מרשימת הארגון:</Typography>
              <TextField fullWidth size="small" placeholder="חפש לפי שם או אימייל..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} sx={{ mb: 2 }}
              />
              {adLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={32} /></Box>
              ) : filteredAdUsers.length === 0 ? (
                <Alert severity="info">לא נמצאו משתמשים {searchQuery ? 'התואמים לחיפוש' : 'נוספים להוספה'}</Alert>
              ) : (
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {filteredAdUsers.map(user => (
                    <Box key={user.email} onClick={() => { setSelectedUser(user); setSelectedRole('viewer'); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, cursor: 'pointer', mb: 0.5, '&:hover': { bgcolor: theme.palette.action.hover }, border: `1px solid ${theme.palette.divider}` }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: '#1565C0', fontSize: '0.8rem' }}>{user.displayName?.charAt(0) || '?'}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{user.displayName}</Typography>
                        <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>משתמש נבחר: <strong>{selectedUser.displayName}</strong> ({selectedUser.email})</Alert>
              <Button size="small" onClick={() => setSelectedUser(null)} sx={{ mb: 2 }}>בחר משתמש אחר</Button>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>בחר הרשאה:</Typography>
              <RoleSelector value={selectedRole} onChange={setSelectedRole} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => { setAddDialogOpen(false); setSelectedUser(null); setSearchQuery(''); }} color="inherit">ביטול</Button>
          <Button variant="contained" onClick={handleAddUser} disabled={!selectedUser}>הוסף</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>שינוי הרשאה — {selectedUser?.displayName}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RoleSelector value={selectedRole} onChange={setSelectedRole} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">ביטול</Button>
          <Button variant="contained" onClick={handleEditRole}>שמור</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
